// FeedbackAdminHost — the impure shell edge of Ops › Feedback
// (`/ops/feedback`). It owns the read (on open, on Try again, and after a
// status change), the two status actions (Mark handled, Reopen: one route
// each), the clipboard and its toast, and the frame; the pure
// `FeedbackAdminPage` gets them as props. The server decides access: a
// 401/403 shows the no-access state. Mounted by `httpMain` for this path.

export { FeedbackAdminHost };

import type { FeedbackAdminReport } from "@bilda/app/feedbackAdmin";
import { Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import {
    FeedbackAdminPage,
    type FeedbackStatusFailure,
} from "../../components/FeedbackAdmin/FeedbackAdminPage.js";
import {
    Toast,
    ToastProvider,
    ToastViewport,
} from "../../components/Toast/Toast.js";
import { TooltipProvider } from "../../components/Tooltip/Tooltip.js";
import { clipboardHttp } from "../httpPorts/clipboard.js";
import { type OpsActionFailure, opsApi } from "../ops/api.js";
import { isDenied, useOpsFrame, useRemote } from "../ops/hooks.js";
import { openReportOf } from "../ops/route.js";

const FeedbackAdminHost = () => {
    const { t } = useLingui();
    const frame = useOpsFrame();
    const [attempt, setAttempt] = useState(0);
    const list = useRemote(opsApi.feedback, attempt);
    const [initiallyOpen] = useState(() =>
        openReportOf(window.location.search),
    );
    const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
    const [failure, setFailure] = useState<FeedbackStatusFailure>();
    const [copied, setCopied] = useState(false);

    const change =
        (
            act: (
                workspaceId: string,
                reportId: string,
            ) => Promise<Result.Result<void, OpsActionFailure>>,
        ) =>
        (r: FeedbackAdminReport) => {
            setFailure(undefined);
            setPending((p) => new Set([...p, r.reportId]));
            act(r.workspaceId, r.reportId).then((res) => {
                setPending(
                    (p) => new Set([...p].filter((id) => id !== r.reportId)),
                );
                if (Result.isErr(res))
                    setFailure({
                        reportId: r.reportId,
                        reason:
                            res.error.type === "ops_refused"
                                ? res.error.reason
                                : undefined,
                    });
                setAttempt((n) => n + 1);
            });
        };

    return (
        <TooltipProvider>
            <ToastProvider>
                <FeedbackAdminPage
                    frame={frame}
                    access={
                        list._tag === "Remote.Failed" && isDenied(list.error)
                            ? "denied"
                            : "allowed"
                    }
                    list={list}
                    initiallyOpen={initiallyOpen}
                    pending={pending}
                    failure={failure}
                    onRetry={() => setAttempt((n) => n + 1)}
                    onCopy={(json) => {
                        clipboardHttp.copyText(json).then((outcome) => {
                            if (outcome === "copied") setCopied(true);
                        });
                    }}
                    onMarkHandled={change(opsApi.markHandled)}
                    onReopen={change(opsApi.reopen)}
                />
                <Toast
                    open={copied}
                    onOpenChange={setCopied}
                    tone="success"
                    title={t`Copied report details`}
                />
                <ToastViewport />
            </ToastProvider>
        </TooltipProvider>
    );
};
